import { useEffect, useRef, useState } from 'react';

import { useToast } from '../context/ToastContext';

/** Phần chung của kết quả đồng bộ mà hook cần đọc. */
interface SyncResult {
  /** Số dòng nhà cái trả về nhưng không đọc nổi — im lặng nuốt là hiện thiếu bản ghi. */
  unreadable: number;
}

interface UseSyncedHistoryOptions<T> {
  /** `null` = chưa mở hộp thoại. Đổi giá trị thì bắt đầu lại từ đầu. */
  accountId: string | null;
  /** Đọc bản đã lưu trong DB. */
  load: (accountId: string) => Promise<T[]>;
  /** Kéo dữ liệu mới từ nhà cái về DB. */
  sync: (accountId: string) => Promise<SyncResult>;
}

interface SyncedHistory<T> {
  items: T[];
  /** Đang đọc DB — chưa có gì để hiện. */
  loading: boolean;
  /** Đang kéo từ nhà cái — đã có dữ liệu cũ để xem trong lúc chờ. */
  syncing: boolean;
}

/**
 * Lịch sử "mở ra là có ngay, mới về thì tự cập nhật".
 *
 * Đọc DB trước để hiện liền, rồi mới đồng bộ với nhà cái và đọc lại. Không có
 * nút bấm: người dùng mở lịch sử ra là để XEM lịch sử, bắt bấm thêm một nút nữa
 * chỉ để thấy đúng thứ vừa xin là công việc thừa.
 *
 * Hai bước chạy NỐI TIẾP chứ không song song. Chạy song song thì cùng lúc kéo
 * toàn bộ lịch sử về hai lần, mà lượt đọc thứ nhất gần như luôn xong trước —
 * tức là trả gấp đôi tải cho đúng một kết quả.
 *
 * Đồng bộ hỏng thì GIỮ NGUYÊN những gì đã đọc được: nhà cái tự xoá lịch sử sau
 * một giờ, nên bản trong DB thường là bản duy nhất còn tồn tại. Xoá trắng bảng
 * chỉ vì một lượt gọi mạng hỏng là tự tay giấu mất dữ liệu thật.
 */
export function useSyncedHistory<T>({
  accountId,
  load,
  sync,
}: UseSyncedHistoryOptions<T>): SyncedHistory<T> {
  const toast = useToast();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // `load`/`sync` là hàm mới ở mỗi lần render nếu chỗ gọi không bọc `useCallback`.
  // Đọc qua ref để `useEffect` chỉ phụ thuộc `accountId` — nếu không thì mỗi lần
  // render lại là một lượt đồng bộ mới, và hộp thoại tự gọi nhà cái không ngừng.
  const loadRef = useRef(load);
  const syncRef = useRef(sync);
  const toastRef = useRef(toast);
  loadRef.current = load;
  syncRef.current = sync;
  toastRef.current = toast;

  useEffect(() => {
    if (accountId === null) return;

    // Đóng hộp thoại hoặc đổi tài khoản giữa chừng thì bỏ kết quả đang bay về:
    // không có cờ này, lượt đồng bộ của tài khoản trước sẽ đổ dữ liệu của nó vào
    // bảng của tài khoản đang mở.
    let alive = true;

    setItems([]);
    setLoading(true);
    setSyncing(true);

    void (async () => {
      try {
        const cached = await loadRef.current(accountId);
        if (!alive) return;
        setItems(cached);
      } catch {
        // Toast lỗi do interceptor bắn. Để bảng rỗng, lượt đồng bộ bên dưới vẫn chạy.
      } finally {
        if (alive) setLoading(false);
      }

      try {
        const result = await syncRef.current(accountId);
        if (!alive) return;
        if (result.unreadable > 0) {
          toastRef.current.error(
            `${String(result.unreadable)} dòng không đọc được — xem log server`,
            6000,
          );
        }

        const refreshed = await loadRef.current(accountId);
        if (!alive) return;
        // Rỗng thì giữ bản cũ: đồng bộ về 0 dòng chỉ nghĩa là "không có gì mới".
        if (refreshed.length > 0) setItems(refreshed);
      } catch {
        // Toast lỗi do interceptor bắn — bảng giữ nguyên bản đã đọc.
      } finally {
        if (alive) setSyncing(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [accountId]);

  return { items, loading, syncing };
}
